import React, { createContext, useContext, useMemo, useState } from "react";
import { BrowserRouter, Link, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "./App.css";

const BUSINESS = { name: "TRIARROW INTERNATIONAL", gstin: "07ABAFT5908K1ZX", address: "1st Floor, Office B (Left Side), Plot No. 9/1, Khasra No. 5/1, Shiv Vihar, Nilothi Mod, Shiv Vihar, New Delhi, West Delhi, Delhi - 110041, India" };

const AuthContext = createContext(null);

const printablePacks = [
  {
    title: "Secret Missions Pack 1",
    age: "Ages 6-10",
    price: "Included",
    theme: "Money, kindness, detective clues, family tasks",
    color: "sunny",
  },
  {
    title: "Animal Alphabet Garden",
    age: "Ages 4-7",
    price: "Premium",
    theme: "Colorful animals, tracing, matching, early reading",
    color: "mint",
  },
  {
    title: "Flower Math Adventures",
    age: "Ages 5-9",
    price: "Premium",
    theme: "Counting, patterns, simple puzzles, reward badges",
    color: "rose",
  },
  {
    title: "Coming Soon: Festival Fun",
    age: "Ages 6-10",
    price: "Coming soon",
    theme: "Indian festivals, culture worksheets, craft prompts",
    color: "lavender",
  },
];

const plans = [
  {
    name: "Starter Bloom",
    price: "INR 199",
    period: "per month",
    description: "Best for trying printable learning at home.",
    features: ["Access to free packs", "3 premium downloads/month", "Email support", "Razorpay-ready checkout"],
  },
  {
    name: "Happy Learner",
    price: "INR 299",
    period: "per month",
    description: "Most useful for regular weekend activities.",
    features: ["12 premium downloads/month", "New activities every month", "Colorful printable PDFs", "Early access to new packs"],
    highlighted: true,
  },
  {
    name: "Family Garden",
    price: "INR 399",
    period: "per month",
    description: "For siblings, cousins, and family learning time.",
    features: ["Unlimited premium downloads", "Bonus festival packs", "Printable certificates", "Priority support"],
  },
];

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const value = useMemo(() => ({ user, setUser }), [user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  return useContext(AuthContext);
}

function Header() {
  const { user, setUser } = useAuth();
  return (
    <header className="site-header">
      <Link className="brand" to="/">
        <span className="brand-mark">LB</span>
        <span>
          <strong>Little Bloom Kids</strong>
          <small>Print. Play. Learn.</small>
        </span>
      </Link>
      <nav className="nav-links" aria-label="Primary navigation">
        <NavLink to="/">Home</NavLink>
        <NavLink to="/printables">Printables</NavLink>
        <NavLink to="/pricing">Pricing</NavLink>
        <NavLink to="/contact">Our business</NavLink>
      </nav>
      <div className="nav-actions">
        {user ? (
          <button className="ghost-button" onClick={() => { setUser(null); toast.success("Signed out"); }}>Logout</button>
        ) : (
          <Link className="primary-button small" to="/login">Sign up / Login</Link>
        )}
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <strong>Little Bloom Kids</strong>
        <p>Printable activities for curious children, busy parents, and joyful learning corners.</p>
        <p>Operated by <strong>{BUSINESS.name}</strong></p>
        <p>GSTIN: {BUSINESS.gstin}</p>
        <p className="business-address">Registered address: {BUSINESS.address}</p>
        <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
      </div>
      <div className="footer-links">
        <Link to="/printables">Printables</Link>
        <Link to="/pricing">Pricing</Link>
        <Link to="/terms">Terms and Conditions</Link>
        <Link to="/contact">Business &amp; Contact</Link>
        <Link to="/login">Account</Link>
      </div>
    </footer>
  );
}

const FREE_PACKS = [
{title:"Little Shape Detective",age:"Ages 3–5",group:"3-5",file:"shape-detective.pdf",theme:"Notice shapes, count carefully, and draw your own picture.",color:"peach",type:"EARLY LEARNING"},
{title:"Kindness Adventure",age:"Ages 5–8",group:"5-8",file:"kindness-adventure.pdf",theme:"Three thoughtful missions, a drawing activity, and a kindness badge.",color:"sage",type:"EVERYDAY MISSIONS"},
{title:"Travel Explorer",age:"Ages 4–8",group:"4-8",file:"travel-explorer.pdf",theme:"A screen-free spotting game and a creative travel journal.",color:"lavender",type:"ON THE GO"}];
function PackCard({pack}) {return <article className="sample-card"><div className={"sample-art "+pack.color}><small>{pack.type}</small><div className="mini-paper"><small>LITTLE BLOOM KIDS</small><strong>{pack.title}</strong><div className="paper-dots"><i/><i/><i/></div><small>LOOK · PLAY · DISCOVER</small></div><span className="free-label">FREE PDF</span></div><div className="sample-body"><p className="pack-meta">{pack.age} · 2 pages</p><h3>{pack.title}</h3><p>{pack.theme}</p><a className="download-link" href={"/downloads/"+pack.file} download={pack.file}>Download free PDF <span>↗</span></a></div></article>}
function Home(){return <><section className="premium-hero"><div><p className="eyebrow">LITTLE MOMENTS. BIG DISCOVERIES.</p><h1>A little less screen.<br/>A little more <em>wonder.</em></h1><p className="hero-description">Beautiful printable activities for curious little minds. Turn an ordinary afternoon, a family trip, or a quiet moment into something worth remembering.</p><div className="hero-actions"><a className="primary-button" href="#free-activities">Explore free activities ↗</a><Link className="text-link" to="/printables">Browse the collection →</Link></div><p className="hero-note">✓ Three free PDFs · No account needed · Print at home</p></div><div className="hero-collage" aria-label="Creative printable activity previews"><p className="collage-label">A little learning, a lot of joy.</p><div className="collage-sheet sheet-back"><small>YOUR NEXT LITTLE ADVENTURE</small><h3>Be a kindness<br/>superhero.</h3><div className="big-heart">♡</div><div className="sheet-line"/></div><div className="collage-sheet sheet-front"><small>LITTLE SHAPE DETECTIVE</small><h3>What can<br/>you discover?</h3><svg viewBox="0 0 240 170" role="img" aria-label="Circle, triangle, square and star"><circle cx="62" cy="55" r="30" fill="#EBC484"/><path d="M140 20 L180 90 L100 90 Z" fill="#7DA58D"/><rect x="34" y="108" width="55" height="48" rx="7" fill="#D89177"/><path d="M153 108 l10 20 22 3-16 15 4 22-20-11-20 11 4-22-16-15 22-3z" fill="#B8ABCA"/></svg><small>LOOK. COUNT. CREATE.</small></div><div className="round-seal">Made for<br/><strong>curious</strong><br/>little minds</div></div></section><div className="value-strip"><span>✦ Screen-free play</span><span>✦ Thoughtfully simple</span><span>✦ Ages 3–8</span><span>✦ Ready to print</span></div><section className="section" id="free-activities"><div className="collection-heading"><div><p className="eyebrow">A SMALL COLLECTION, A LOVELY START</p><h2>Your next little adventure.</h2></div><Link className="text-link" to="/printables">View all activities ↗</Link></div><div className="sample-grid">{FREE_PACKS.map(p=><PackCard pack={p} key={p.file}/>)}</div></section><section className="ritual-section"><div><p className="eyebrow">LESS PREPARATION. MORE CONNECTION.</p><h2>Make room for<br/><em>the small things.</em></h2><p>You don't need a perfect plan. Just a printed page, a few pencils, and a little time together.</p></div><div>{[["01","Pick a little adventure","Choose an activity that suits your child's age and mood."],["02","Print and get comfortable","Use A4 paper, pencils, and whatever colours you have."],["03","Let curiosity lead","Explore together. There is no rush and no perfect result."]].map(([n,t,b])=><div className="ritual-step" key={n}><span>{n}</span><div><h3>{t}</h3><p>{b}</p></div></div>)}</div></section><section className="section moments-section"><p className="eyebrow">WHEREVER THE DAY TAKES YOU</p><h2>Good company for little hands.</h2><div className="moments-grid">{[["At home","A creative pause between busy moments.","⌂"],["On a family trip","A spotting game for the journey.","↗"],["While you wait","A quiet activity at a restaurant or airport.","◷"]].map(([t,b,i])=><article key={t}><span>{i}</span><h3>{t}</h3><p>{b}</p></article>)}</div></section><section className="section faq-section"><div><p className="eyebrow">A FEW HELPFUL ANSWERS</p><h2>Before you print.</h2></div><div>{[["Are the PDFs really free?","Yes. All three sample activities are free to download, with no signup or payment required."],["What do I need?","A printer, A4 paper, a pencil, and optional colours. Each PDF includes parent guidance and an age range."],["Can I print for my classroom?","You may print copies for your own children or students. Please do not resell or redistribute the digital files."],["Do I need a colour printer?","No. Clear outlines and written instructions make black-and-white printing suitable too."]].map(([q,a])=><details key={q}><summary>{q}<span>+</span></summary><p>{a}</p></details>)}</div></section><section className="closing-banner"><p className="eyebrow">START WITH ONE SMALL MOMENT</p><h2>Something lovely is just a print away.</h2><a className="primary-button" href="#free-activities">Choose your free activity ↗</a></section></>}
function Printables(){const [q,setQ]=useState("");const [age,setAge]=useState("all");const packs=FREE_PACKS.filter(p=>(age==="all"||age===p.group)&&(p.title+" "+p.theme).toLowerCase().includes(q.toLowerCase()));return <section className="section page-section"><div className="section-heading centered"><p className="eyebrow">THE PRINTABLE COLLECTION</p><h1>Small pages.<br/><em>Big possibilities.</em></h1><p>Three carefully made activities to try at home or on the go. Free to download. Yours to explore.</p></div><div className="collection-tools"><label>Find an activity<input type="search" placeholder="Search shapes, kindness, travel…" value={q} onChange={e=>setQ(e.target.value)}/></label><label>Age range<select value={age} onChange={e=>setAge(e.target.value)}><option value="all">All ages</option><option value="3-5">Ages 3–5</option><option value="5-8">Ages 5–8</option><option value="4-8">Ages 4–8</option></select></label></div><div className="sample-grid">{packs.map(p=><PackCard pack={p} key={p.file}/>)}</div>{!packs.length&&<p>No activities match. Try a different search or age range.</p>}<p className="download-guidance">A4 PDFs · Parent guidance included · Black-and-white friendly · Adult supervision recommended</p></section>}

function Pricing() {
  return (
    <section className="section page-section">
      <div className="section-heading centered">
        <p className="eyebrow">Simple pricing</p>
        <h1>Subscription plans ready for Razorpay integration.</h1>
        <p>These plans can be connected with Razorpay Checkout or subscription APIs when you are ready.</p>
      </div>
      <div className="pricing-grid">
        {plans.map((plan) => (
          <article className={plan.highlighted ? "price-card highlighted" : "price-card"} key={plan.name}>
            {plan.highlighted && <span className="popular">Most popular</span>}
            <h3>{plan.name}</h3>
            <p>{plan.description}</p>
            <div className="price"><strong>{plan.price}</strong><span>{plan.period}</span></div>
            <ul>{plan.features.map((item) => <li key={item}>{item}</li>)}</ul>
            <Link className="primary-button full" to="/login">Start now</Link>
          </article>
        ))}
      </div>
    </section>
  );
}

function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  function submitForm(event) {
    event.preventDefault();
    setUser({ name: form.name || "Parent", email: form.email });
    toast.success(mode === "signup" ? "Account created for demo" : "Logged in for demo");
    navigate("/printables");
  }

  return (
    <section className="auth-wrap">
      <div className="auth-card">
        <div className="mode-switch">
          <button className={mode === "signup" ? "active" : ""} onClick={() => setMode("signup")}>Sign up</button>
          <button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Login</button>
        </div>
        <h1>{mode === "signup" ? "Create parent account" : "Welcome back"}</h1>
        <p>{user ? "You are already signed in for this demo." : "Use this form to show visitors that account access is available before premium PDF downloads."}</p>
        <form onSubmit={submitForm}>
          {mode === "signup" && <label>Parent name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" /></label>}
          <label>Email<input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="parent@example.com" /></label>
          <label>Password<input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 characters" /></label>
          <button className="primary-button full" type="submit">{mode === "signup" ? "Create account" : "Login"}</button>
        </form>
        <p className="fine-print">By continuing, users agree to the Terms and Conditions and understand paid downloads will be enabled through Razorpay.</p>
      </div>
    </section>
  );
}

function Terms() {
  const sections = [
    ["Business details", `This website is operated by ${BUSINESS.name}, a partnership. GSTIN: ${BUSINESS.gstin}. Registered address: ${BUSINESS.address}.`],
    ["Use of website", "Little Bloom Kids provides printable educational PDFs and related digital content for children under parent or guardian supervision. Users must provide accurate account information and keep login details secure."],
    ["Digital downloads", "PDFs are for personal, home, classroom, or small-group educational use only. Users may print copies for their own children or students, but may not resell, redistribute, upload, or claim the content as their own."],
    ["Payments", "Paid plans and premium downloads may be processed through Razorpay or another approved payment partner. Prices, plan benefits, and availability may change before or after launch, but active users will see the applicable price before payment."],
    ["Refunds", "Because printable PDFs are digital products, completed downloads are generally non-refundable. If a payment is charged incorrectly or access fails, users can contact support for review and resolution."],
    ["Parent responsibility", "Activities are designed for learning and play, but parents or guardians are responsible for choosing age-appropriate worksheets, supervising scissors, glue, colors, or craft materials, and ensuring safe use."],
    ["Content accuracy", "We try to keep activities useful and age appropriate, but the content is not a substitute for school curriculum, professional educational advice, medical advice, or child development assessment."],
    ["Account suspension", "We may restrict access if an account is used for sharing paid content publicly, misuse, attempted fraud, abusive behavior, or activity that affects the website or other users."],
    ["Privacy", "Basic account, payment, and usage information may be collected to operate the service, provide downloads, improve content, and handle support. Payment details are handled by the payment provider and are not meant to be stored directly by this website."],
    ["Changes to terms", "These terms may be updated as the website grows, including when Razorpay subscriptions and premium downloads go live. Continued use of the website means the updated terms are accepted."],
    ["Contact", `For business correspondence, refund review, access issues, or content questions, write to ${BUSINESS.name} at ${BUSINESS.address}. Please include your order or account reference when applicable.`],
  ];
  return (
    <section className="section page-section terms-page">
      <div className="section-heading centered">
        <p className="eyebrow">Website policy</p>
        <h1>Terms and Conditions</h1>
        <p>Last updated: 8 October 2026. These terms apply to Little Bloom Kids, operated by TRIARROW INTERNATIONAL.</p>
      </div>
      <div className="terms-list">
        {sections.map(([title, body], index) => (
          <article className="term-item" key={title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><h2>{title}</h2><p>{body}</p></div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ComingSoon() {
  return (
    <section className="section page-section centered simple-page">
      <p className="eyebrow">Coming soon</p>
      <h1>More printable adventures are blooming.</h1>
      <p>Festival packs, senior activities, and themed bundles can be added here when ready.</p>
      <Link className="primary-button" to="/printables">Back to printables</Link>
    </section>
  );
}

function BusinessContact() {
  return (
    <section className="section page-section terms-page">
      <div className="section-heading centered">
        <p className="eyebrow">Business information</p>
        <h1>Business &amp; Contact</h1>
        <p>Little Bloom Kids is operated by {BUSINESS.name}.</p>
      </div>
      <div className="terms-list">
        <article className="term-item"><span>01</span><div><h2>Registered business</h2><p>{BUSINESS.name}</p><p>Constitution: Partnership</p><p>GSTIN: {BUSINESS.gstin}</p></div></article>
        <article className="term-item"><span>02</span><div><h2>Registered address</h2><p>{BUSINESS.address}</p></div></article>
        <article className="term-item"><span>03</span><div><h2>Business correspondence</h2><p>For support, payment, download, or content enquiries, write to the registered address above. Include your order or account reference where applicable.</p></div></article>
      </div>
    </section>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="app-shell">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/printables" element={<Printables />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<BusinessContact />} />
              <Route path="/coming-soon" element={<ComingSoon />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
